import { Link } from 'react-router-dom'
import { CourseCard } from '../components/CourseCard'
import { Icon } from '../components/Icon'
import { MODULES, MODULE_BY_ID } from '../data/catalog'
import { COURSES } from '../data/courses'
import { useProgress } from '../state/ProgressContext'
import { AUTHOR, APP_VERSION_LABEL } from '../version'

export function LandingPage() {
  const { progress } = useProgress()
  const completed = Object.keys(progress.completedSections).length
  const totalSections = MODULES.reduce((sum, module) => sum + module.sections.length, 0)
  const resume = progress.lastPosition ? MODULE_BY_ID.get(progress.lastPosition.moduleId) : null
  const resumeSection = resume?.sections.find((section) => section.sectionId === progress.lastPosition?.sectionId)
  return (
    <>
      <section className="landing-hero page-width">
        <div className="hero-copy">
          <span className="eyebrow">Eight focused courses · 105 complete modules</span>
          <h1>Learn Investing: From the Basics to <span>Advanced Planning</span></h1>
          <p>Understand the principles that apply broadly, then see how age, family, taxes, disability, account type, and time horizon change the answer.</p>
          <div className="hero-actions">
            {resume ? (
              <Link className="button primary" to={`/modules/${resume.id}#${resumeSection?.sectionId ?? resume.sections[0].sectionId}`}>
                Continue {resume.id} · {resumeSection?.label ?? resume.title}<Icon name="chevron" size={17} />
              </Link>
            ) : <Link className="button primary" to="/courses/investing-essentials">Start with Investing Essentials<Icon name="chevron" size={17} /></Link>}
            <Link className="button" to="/search"><Icon name="search" size={17} />Search all modules</Link>
          </div>
          <div className="hero-stats">
            <span><strong>105</strong> modules</span>
            <span><strong>{totalSections.toLocaleString()}</strong> sections</span>
            <span><strong>173</strong> calculators</span>
            <span><strong>{completed}</strong> completed</span>
          </div>
        </div>
        <div className="hero-visual" aria-hidden="true">
          <div className="visual-card visual-main">
            <span>Build understanding</span>
            <svg viewBox="0 0 300 180">
              <path className="grid-line" d="M25 30h250M25 70h250M25 110h250M25 150h250" />
              <path className="chart-area" d="M25 150 75 132l45-28 44 8 48-53 63-36v127H25Z" />
              <path className="chart-line" d="M25 150 75 132l45-28 44 8 48-53 63-36" />
              <circle cx="275" cy="23" r="6" />
            </svg>
          </div>
          <div className="visual-card visual-note"><Icon name="check" size={17} />Context changes the recommendation</div>
          <div className="visual-card visual-note second"><Icon name="book" size={17} />Learn at your own pace</div>
        </div>
        <div className="hero-signature">{AUTHOR} · {APP_VERSION_LABEL}</div>
      </section>

      <section className="journey page-width" aria-label="Learning journey">
        {['Understand', 'Plan', 'Save tax', 'Build', 'Property', 'Retire', 'Protect', 'Act'].map((item, index) => (
          <span key={item}><b>{index + 1}</b>{item}</span>
        ))}
      </section>

      <section className="course-section page-width">
        <div className="section-heading">
          <div><span className="eyebrow">Choose the question in front of you</span><h2>Eight manageable courses</h2></div>
          <p>You do not need to complete everything in order. Each course keeps related modules together and shows your progress.</p>
        </div>
        <div className="course-grid">{COURSES.map((course) => <CourseCard key={course.id} course={course} />)}</div>
      </section>

      <section className="principles page-width">
        <div><Icon name="book" /><h3>Common foundation</h3><p>Learn definitions, risk, diversification, costs, taxes, behavior, and the difference between saving and investing.</p></div>
        <div><Icon name="search" /><h3>Context before conclusions</h3><p>Compare approaches and understand why the same idea can lead to different recommendations for different households.</p></div>
        <div><Icon name="ai" /><h3>Optional AI help</h3><p>The course works without AI. Sign in only if you choose deeper explanations, semantic search, hints, or cross-device sync.</p></div>
      </section>

      <aside className="financial-disclaimer page-width">
        <strong>Educational material—not financial advice.</strong>
        <span> Nothing here is a recommendation to buy or sell an investment. Tax, legal, investment, and benefit rules change. Confirm current information with primary sources or an appropriately qualified professional.</span>
      </aside>
    </>
  )
}
